import { FilePlus2, Layers, Save, FolderOpen, Presentation, Boxes, FileDown, type LucideIcon } from 'lucide-react';
import { PERM, useAbility, type Perm } from 'shared/access';
import type { LayoutApi } from 'app/shell/LayoutProvider';
import type { ConfigActions } from 'shared/state/configContexts';
import type { QuoteActionsValue } from 'shared/state/quoteActionsContext';
import type { FlatConfig } from 'domain/index';

export type SpineGroup = 'configuration' | 'views';

export type SpineApi = { layout: LayoutApi; actions: ConfigActions; persistence: { resetConfig: (systemType?: 'BaseSystem' | 'Expansion') => void } | null; quote: QuoteActionsValue; config: FlatConfig };

export type SpineActionDef = {
  id: string;
  group: SpineGroup;
  label: string;
  icon: LucideIcon;
  requires: Perm[];
  /** Phase that wires onSelect for real. Earlier phases render the item disabled with a title. */
  availableFrom: 1 | 2 | 5 | 6;
  /** Opens the overlay whose id is this action's id; the spine marks it while that overlay is open. */
  overlay?: boolean;
  onSelect: (api: SpineApi) => void;
};

export const SPINE_ACTIONS: SpineActionDef[] = [
  { id: 'new', group: 'configuration', label: 'New System', icon: FilePlus2, requires: [], availableFrom: 1,
    // A new configuration starts at the top of the page, not wherever the old one was scrolled to.
    onSelect: ({ persistence, layout }) => { persistence?.resetConfig('BaseSystem'); layout.scrollToTop(); } },
  // The two ways to start over, and nothing else: until 2026-10-04 this one was a
  // toggle that flipped the CURRENT configuration between base and expansion.
  { id: 'expansion', group: 'configuration', label: 'New Expansion', icon: Layers, requires: [], availableFrom: 2,
    onSelect: ({ persistence, layout }) => { persistence?.resetConfig('Expansion'); layout.scrollToTop(); } },
  { id: 'save', group: 'configuration', label: 'Save', icon: Save, requires: [], availableFrom: 6, onSelect: ({ quote }) => quote.setShowSaveDialog(true) },
  { id: 'load', group: 'configuration', label: 'Load', icon: FolderOpen, requires: [], availableFrom: 6, onSelect: ({ quote }) => quote.setShowQuotesModal(true) },
  // The slide and the PDF sit in the top group, straight after Load, so they are in
  // reach without scrolling a long sidebar (tester note, 2026-10-04).
  // Both export straight away and are labelled for what they do. Until 2026-10-05 the
  // slide opened a preview overlay with its own Export PPTX button.
  { id: 'systemSlide', group: 'configuration', label: 'Export System Slide', icon: Presentation, requires: [], availableFrom: 5, onSelect: ({ quote }) => quote.handleGenerateRackSlide() },
  { id: 'systemPdf', group: 'configuration', label: 'Export Summary', icon: FileDown, requires: [PERM.SUMMARY_PDF], availableFrom: 6, onSelect: ({ quote }) => quote.handleGenerateSystemPDF() },
  { id: 'rackView', group: 'views', label: 'Rack View', icon: Boxes, requires: [], availableFrom: 5, overlay: true, onSelect: ({ layout }) => layout.openOverlay('rackView') },
];

export function useVisibleSpineActions(): SpineActionDef[] {
  const ability = useAbility();
  return SPINE_ACTIONS.filter((a) => a.requires.every((p) => ability.can(p)));
}
